/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useMemo } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

interface CalendarPickerProps {
    value: Date | null;
    onChange: (date: Date) => void;
    minDate: Date;
}

const TIME_SLOTS = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'
];

const CalendarPicker: React.FC<CalendarPickerProps> = ({ value, onChange, minDate }) => {
    const { t } = useTranslation();
    const [viewDate, setViewDate] = useState(value || new Date());

    const selectedDate = value ? value.getDate() : null;
    const selectedMonth = value ? value.getMonth() : null;
    const selectedYear = value ? value.getFullYear() : null;
    const selectedTime = value ? `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}` : null;

    const monthName = t(`month_${viewDate.toLocaleString('default', { month: 'short' }).toLowerCase()}` as any);
    const year = viewDate.getFullYear();

    const daysInMonth = useMemo(() => {
        const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
        const days = [];
        while (date.getMonth() === viewDate.getMonth()) {
            days.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }, [viewDate]);

    const firstDayOfMonth = useMemo(() => {
        return new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
    }, [viewDate]);

    const handlePrevMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const handleDateSelect = (day: number) => {
        const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        if (value) {
            newDate.setHours(value.getHours());
            newDate.setMinutes(value.getMinutes());
        }
        onChange(newDate);
    };

    const handleTimeSelect = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        const newDate = value ? new Date(value) : new Date();
        if (!value) {
            // If no date is selected, default to today if valid, otherwise minDate
            const today = new Date();
            if (today < minDate) {
                newDate.setFullYear(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
            }
        }
        newDate.setHours(hours, minutes, 0, 0);
        onChange(newDate);
    };

    const weekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    return (
        <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
                <button type="button" onClick={handlePrevMonth} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeftIcon className="w-5 h-5" /></button>
                <div className="font-semibold text-lg">{monthName} {year}</div>
                <button type="button" onClick={handleNextMonth} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRightIcon className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                {weekdays.map(day => <div key={day}>{t(`day_${day}` as any)}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 place-items-center">
                {Array(firstDayOfMonth).fill(null).map((_, i) => <div key={`empty-${i}`} />)}
                {daysInMonth.map(day => {
                    const date = day.getDate();
                    const isSelected = date === selectedDate && viewDate.getMonth() === selectedMonth && viewDate.getFullYear() === selectedYear;
                    const isSunday = day.getDay() === 0; // Sunday = 0
                    const isDisabled = (day < minDate && day.toDateString() !== minDate.toDateString()) || isSunday;
                    return (
                        <button
                            type="button"
                            key={date}
                            onClick={() => handleDateSelect(date)}
                            disabled={isDisabled}
                            className={`w-10 h-10 rounded-full text-sm transition-colors ${isSelected ? 'bg-indigo-600 text-white font-bold' :
                                    isDisabled ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' :
                                        'hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                        >
                            {date}
                        </button>
                    )
                })}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold mb-3">{t('calendar_selectTime')}</h4>
                <div className="grid grid-cols-3 gap-2">
                    {TIME_SLOTS.map(time => {
                        const isSelected = time === selectedTime;
                        return (
                            <button
                                type="button"
                                key={time}
                                onClick={() => handleTimeSelect(time)}
                                className={`py-2 px-3 rounded-md text-sm font-semibold transition-colors ${isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                {time}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    );
};

export default CalendarPicker;